import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../../types/Property/Property'
import routes from '../../../config/routes'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import { formatPercentage } from '../../../lib/utils/formatNumber'
import Link from 'next/link'
import React from 'react'
import { useTranslation } from 'next-i18next'
import { Tooltip } from 'react-tooltip'

interface NavigationPropertyPreviewProps {
  property: PropertyWithAccess
  onClick?: (property: PropertyWithAccess) => void
}

export default function NavigationPropertyPreview({
  property,
  onClick,
}: NavigationPropertyPreviewProps) {
  const { t } = useTranslation('common')

  if (property.allowed) {
    const p = property as Property
    return (
      <Link
        href={routes.property.replace('[propertyId]', property.id)}
        onClick={() => (onClick ? onClick(property) : null)}
      >
        <div className="relative flex text-neutralBlack last:border-b-0 hover:cursor-pointer hover:bg-brandDarkPurple hover:text-white">
          <div className="w-1/3">
            {p && p.images && p.images.length > 0 && (
              <img
                src={p.images[0].url}
                alt={p.addressStreet + ' photo'}
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="w-2/3">
            <div className="px-2 py-3">
              <div className="flex text-sm">{p.translation.title}</div>

              <div className="border-indigo-200/20 pt-3">
                {p.revaluationPerYear > 0 ? (
                  <div className="mb-1 flex text-xs">
                    <HeroIcons.ChartBarIcon
                      className="mr-1 h-3 w-3"
                      aria-hidden="true"
                    />
                    {t('propertyCard.estimatedPriceReassessment')}{' '}
                    <div className="ml-1 font-bold">
                      {formatPercentage(p.revaluationPerYear, 2, true)}
                    </div>
                  </div>
                ) : null}
                <div className="flex text-xs">
                  <HeroIcons.BanknotesIcon
                    className="mr-1 h-3 w-3"
                    aria-hidden="true"
                  />
                  {t('propertyCard.estimatedRentalYields')}{' '}
                  <div className="ml-1 font-bold">
                    {formatPercentage(p.rentalYieldPerYear, 2, true)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  const p = property as MaskedProperty
  return (
    <div className="relative flex">
      <div className="w-1/3">
        {p && p.images && p.images.length > 0 && (
          <img
            src={p.images[0].url}
            alt={p.translation.title + ' photo'}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="w-2/3">
        <div className="p-1">
          <div className="rounded-md bg-walkthrough-pattern bg-cover bg-center p-1.5">
            <div>
              <div className="flex">
                <div>
                  <p className="text-xs text-gray-100">Club deal exclusif</p>
                </div>

                <div className="flex grow justify-end">
                  <HeroIcons.InformationCircleIcon
                    data-tooltip-id="clubdeal-previewbar-tooltip"
                    data-tooltip-place="top"
                    className="h-auto w-4 cursor-pointer text-gray-100"
                  />
                </div>
              </div>

              <div className="flex grow items-center">
                <p className="text-md font-medium text-white">{p.club.name}</p>
              </div>
            </div>
          </div>

          <Tooltip
            place="left"
            id="clubdeal-previewbar-tooltip"
            className="z-50 max-w-[250px] text-center leading-snug"
          >
            <span className="font-medium text-white">
              {t('clubDeal.propertyCardTooltip.title')}
            </span>
            <br />
            <span className="text-xs text-gray-200">
              {t('clubDeal.propertyCardTooltip.text1')}
              <br />
              {t('clubDeal.propertyCardTooltip.text2')}
            </span>
          </Tooltip>
        </div>

        <div className="px-2 py-3">
          <div className="flex text-sm">{p.translation.title}</div>

          <div className="border-indigo-200/20 pt-3">
            <div className="flex text-xs">
              <HeroIcons.BanknotesIcon
                className="mr-1 h-3 w-3"
                aria-hidden="true"
              />
              {t('propertyCard.estimatedRentalYields')}{' '}
              <div className="ml-1 font-bold">
                {formatPercentage(p.rentalYieldPerYear, 2, true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
